import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { FragmentsModels } from "@thatopen/fragments";
import { DebugOverlay, type FragDebugInfo } from "./DebugOverlay";
import { LoadStatusOverlay } from "./LoadStatusOverlay";
import { fitCameraToScene } from "./sceneBounds";
import type { FragBimViewerProps, FragViewerLoadStatus, LoadedModel, SceneCtx } from "./types";
import {
  applyColorRulesToModels,
  applyVisibilityFilterToModels,
} from "./visibilityFilter";
import {
  debugLog,
  FLOOR_CLIP_BOTTOM_TOLERANCE_M,
  FLOOR_CLIP_TOP_TOLERANCE_M,
  getCappedPixelRatio,
  getModelInputId,
  getRetainedModelIds,
  IDLE_PIXEL_RATIO_CAP,
  INTERACTION_PIXEL_RATIO_CAP,
  yieldToBrowser,
} from "./viewerUtils";

export type {
  FragBimViewerProps,
  FragColorRule,
  FragFilter,
  FragManifestEntry,
  FragModelInput,
  IFloorClip,
  ISyncCamera,
} from "./types";

const FragBimViewer: React.FC<FragBimViewerProps> = ({
  models,
  filter,
  colorRules,
  syncCamera,
  onCameraChange,
  onLoadedModelIdsChange,
  debugOverlay = false,
  floorClip,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SceneCtx | null>(null);
  const animFrameRef = useRef<number>(0);
  const needsRenderRef = useRef(true);
  const clipPlaneCountRef = useRef(0);
  const hasFitCameraRef = useRef(false);
  const loadingModelIdsRef = useRef<Set<string>>(new Set());
  const desiredModelIdsRef = useRef<Set<string>>(new Set());
  const lastLoadStatusUpdateRef = useRef(0);
  const filterRef = useRef(filter);
  const [sceneReady, setSceneReady] = useState(false);
  const [loadedModelVersion, setLoadedModelVersion] = useState(0);
  const [loadStatus, setLoadStatus] = useState<FragViewerLoadStatus | null>({
    title: "Startar 3D-vy",
    detail: "Initierar renderare och 3D-worker...",
    blocking: true,
  });
  const modelsKey = useMemo(
    () => models?.map((model) => getModelInputId(model)).join("|") ?? "",
    [models]
  );
  const setLoadStatusThrottled = (status: FragViewerLoadStatus | null, force = false) => {
    const now = performance.now();
    if (!force && now - lastLoadStatusUpdateRef.current < 120) return;
    lastLoadStatusUpdateRef.current = now;
    setLoadStatus(status);
  };

  // Refs so the animate loop (created once) always reads latest prop values
  const syncCameraRef = useRef(syncCamera);
  const onCameraChangeRef = useRef(onCameraChange);
  const onLoadedModelIdsChangeRef = useRef(onLoadedModelIdsChange);
  const debugOverlayRef = useRef(debugOverlay);
  useEffect(() => { filterRef.current = filter; }, [filter]);
  useEffect(() => { syncCameraRef.current = syncCamera; }, [syncCamera]);
  useEffect(() => { onCameraChangeRef.current = onCameraChange; }, [onCameraChange]);
  useEffect(() => { onLoadedModelIdsChangeRef.current = onLoadedModelIdsChange; }, [onLoadedModelIdsChange]);
  useEffect(() => { debugOverlayRef.current = debugOverlay; }, [debugOverlay]);

  const notifyRetainedModelIds = (loadedModels: Map<string, LoadedModel>) => {
    onLoadedModelIdsChangeRef.current?.(
      getRetainedModelIds(loadedModels.keys(), loadingModelIdsRef.current)
    );
  };

  // --- Floor clipping ---
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx) return;

    const nextClipPlaneCount = floorClip ? 2 : 0;
    if (floorClip) {
      ctx.globalClipBottom.normal.set(0, 1, 0);
      ctx.globalClipBottom.constant = -(floorClip.bottom - FLOOR_CLIP_BOTTOM_TOLERANCE_M);

      ctx.globalClipTop.normal.set(0, -1, 0);
      ctx.globalClipTop.constant = floorClip.top - FLOOR_CLIP_TOP_TOLERANCE_M;

      ctx.renderer.clippingPlanes = [ctx.globalClipBottom, ctx.globalClipTop];
    } else {
      ctx.renderer.clippingPlanes = [];
    }

    if (clipPlaneCountRef.current !== nextClipPlaneCount) {
      clipPlaneCountRef.current = nextClipPlaneCount;
      for (const entry of ctx.loadedModels.values()) {
        entry.model.object.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (!mesh.isMesh) return;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mat of mats) {
            (mat as THREE.Material).needsUpdate = true;
          }
        });
      }
    }

    needsRenderRef.current = true;
  }, [floorClip, sceneReady, loadedModelVersion]);

  // Debug overlay state — updated imperatively from the animate loop (throttled)
  const [debugInfo, setDebugInfo] = React.useState<FragDebugInfo | null>(null);
  const debugFrameRef = useRef(0);

  // --- Scene setup (once on mount) ---
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);

    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.5,
      10000
    );
    camera.position.set(10, 10, 10);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(getCappedPixelRatio(window.devicePixelRatio, IDLE_PIXEL_RATIO_CAP));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.localClippingEnabled = false;
    renderer.sortObjects = false;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;
    let restorePixelRatioTimer: number | undefined;
    const setRendererPixelRatio = (cap: number) => {
      renderer.setPixelRatio(getCappedPixelRatio(window.devicePixelRatio, cap));
      needsRenderRef.current = true;
    };
    const handleControlsStart = () => {
      if (restorePixelRatioTimer != null) window.clearTimeout(restorePixelRatioTimer);
      setRendererPixelRatio(INTERACTION_PIXEL_RATIO_CAP);
    };
    const handleControlsEnd = () => {
      if (restorePixelRatioTimer != null) window.clearTimeout(restorePixelRatioTimer);
      restorePixelRatioTimer = window.setTimeout(() => {
        setRendererPixelRatio(IDLE_PIXEL_RATIO_CAP);
      }, 120);
    };
    const handleControlsChange = () => {
      needsRenderRef.current = true;
    };
    controls.addEventListener("start", handleControlsStart);
    controls.addEventListener("end", handleControlsEnd);
    controls.addEventListener("change", handleControlsChange);

    scene.add(new THREE.AmbientLight(0xffffff, 1));
    const dirLight = new THREE.DirectionalLight(0xffffff, 2);
    dirLight.position.set(10, 20, 10);
    scene.add(dirLight);

    const ro = new ResizeObserver(() => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
      needsRenderRef.current = true;
    });
    ro.observe(container);

    const tmpDir = new THREE.Vector3();
    const tmpTarget = new THREE.Vector3();
    const tmpPos = new THREE.Vector3();

    // Log initial camera state
    camera.getWorldDirection(tmpDir);
    debugLog("[FragBimViewer] initial camera", {
      position: camera.position.toArray().map((v) => +v.toFixed(3)),
      direction: tmpDir.toArray().map((v) => +v.toFixed(3)),
      target: controls.target.toArray().map((v) => +v.toFixed(3)),
      distance: +camera.position.distanceTo(controls.target).toFixed(3),
    });

    let lastLogKey = "";
    let lastSyncLogKey = "";

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);

      const sync = syncCameraRef.current;
      let shouldRender = needsRenderRef.current;
      if (sync) {
        // Driven mode: position camera from 2D viewpoint
        controls.enabled = false;
        // After Y/Z child swap: IFC (x, y) → Three.js (x, zFloor, y)
        tmpTarget.set(sync.viewPoint.x, sync.zFloor, sync.viewPoint.y);
        // Fixed 45° downward angle: equal horizontal (+Z) and vertical (-Y) components
        const INV_SQRT2 = 0.7071067811865476;
        tmpDir.set(0, -INV_SQRT2, INV_SQRT2);
        tmpPos.copy(tmpTarget).addScaledVector(tmpDir, -sync.distance);
        camera.position.copy(tmpPos);
        camera.lookAt(tmpTarget);
        controls.target.copy(tmpTarget);
        shouldRender = true;

        const syncKey = `${sync.viewPoint.x.toFixed(1)},${sync.viewPoint.y.toFixed(1)}`;
        if (syncKey !== lastSyncLogKey) {
          lastSyncLogKey = syncKey;
          debugLog("[FragBimViewer] sync camera — 2D viewpoint driving 3D", {
            viewPoint2d: { x: +sync.viewPoint.x.toFixed(3), y: +sync.viewPoint.y.toFixed(3) },
            threeJsTarget: [+tmpTarget.x.toFixed(3), +tmpTarget.y.toFixed(3), +tmpTarget.z.toFixed(3)],
            cameraPos: [+tmpPos.x.toFixed(3), +tmpPos.y.toFixed(3), +tmpPos.z.toFixed(3)],
            distance: +sync.distance.toFixed(3),
          });
        }
      } else {
        // Free mode: let OrbitControls drive
        controls.enabled = true;
        if (controls.update()) shouldRender = true;
        camera.getWorldDirection(tmpDir);
        const dist = camera.position.distanceTo(controls.target);
        if (shouldRender) {
          onCameraChangeRef.current?.([tmpDir.x, tmpDir.y, tmpDir.z], dist);
        }

        // Log only when camera meaningfully moves (1dp resolution to avoid noise)
        const logKey = [
          camera.position.x.toFixed(1),
          camera.position.y.toFixed(1),
          camera.position.z.toFixed(1),
          tmpDir.x.toFixed(2),
          tmpDir.y.toFixed(2),
          tmpDir.z.toFixed(2),
        ].join(",");
        if (shouldRender && logKey !== lastLogKey) {
          lastLogKey = logKey;
          debugLog("[FragBimViewer] camera move", {
            position: camera.position.toArray().map((v) => +v.toFixed(3)),
            direction: tmpDir.toArray().map((v) => +v.toFixed(3)),
            target: controls.target.toArray().map((v) => +v.toFixed(3)),
            distance: +dist.toFixed(3),
          });
        }
      }

      if (shouldRender) {
        renderer.render(scene, camera);
        needsRenderRef.current = false;
      }

      // Debug overlay — throttled to every 6 frames (~10fps) to avoid flooding React
      if (debugOverlayRef.current && shouldRender) {
        debugFrameRef.current += 1;
        if (debugFrameRef.current % 6 === 0) {
          camera.getWorldDirection(tmpDir);
          const sync2 = syncCameraRef.current;
          setDebugInfo({
            camPos: camera.position.clone(),
            camDir: tmpDir.clone(),
            target: controls.target.clone(),
            syncTarget: sync2
              ? new THREE.Vector3(sync2.viewPoint.x, sync2.zFloor, sync2.viewPoint.y)
              : null,
          });
        }
      }
    };

    let cancelled = false;
    setLoadStatus({
      title: "Startar 3D-vy",
      detail: "Hämtar 3D-worker...",
      blocking: true,
    });

    (async () => {
      const workerUrl = await FragmentsModels.getWorker();
      if (cancelled) { debugLog("[FragBimViewer] scene init cancelled before fragments"); return; }
      const fragments = new FragmentsModels(workerUrl);
      fragments.settings.autoCoordinate = true;
      sceneRef.current = {
        fragments,
        scene,
        camera,
        renderer,
        controls,
        loadedModels: new Map(),
        globalClipBottom: new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
        globalClipTop: new THREE.Plane(new THREE.Vector3(0, -1, 0), 0),
      };
      debugLog("[FragBimViewer] scene ready");
      setSceneReady(true);
      animate();
    })().catch((error) => {
      console.error("[FragBimViewer] failed to initialize scene", error);
      if (!cancelled) {
        setLoadStatus({
          title: "Kunde inte starta 3D-vyn",
          detail: error instanceof Error ? error.message : "Ett okänt fel uppstod.",
          blocking: true,
          tone: "error",
        });
      }
    });

    return () => {
      debugLog("[FragBimViewer] scene teardown");
      cancelled = true;
      if (restorePixelRatioTimer != null) window.clearTimeout(restorePixelRatioTimer);
      controls.removeEventListener("start", handleControlsStart);
      controls.removeEventListener("end", handleControlsEnd);
      controls.removeEventListener("change", handleControlsChange);
      onLoadedModelIdsChangeRef.current?.([]);
      cancelAnimationFrame(animFrameRef.current);
      ro.disconnect();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      sceneRef.current = null;
      setSceneReady(false);
    };
  }, []);

  useEffect(() => {
    if (sceneReady && !models?.length) setLoadStatus(null);
  }, [sceneReady, models?.length]);

  // --- Load models when scene is ready and models prop changes ---
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!sceneReady || !ctx || !models?.length) return;

    const { fragments, scene, loadedModels } = ctx;
    const requestedModelIds = new Set(models.map(getModelInputId));
    desiredModelIdsRef.current = requestedModelIds;
    let cancelled = false;
    const effectLoadingModelIds = new Set<string>();

    (async () => {
      debugLog("[FragBimViewer] models effect running, count:", models.length);
      let changed = false;

      // Dispose models no longer in the list
      for (const [id, entry] of loadedModels) {
        if (!requestedModelIds.has(id)) {
          scene.remove(entry.model.object);
          await fragments.disposeModel(id);
          loadedModels.delete(id);
          changed = true;
        }
      }
      if (changed) notifyRetainedModelIds(loadedModels);
      if (loadedModels.size === 0) hasFitCameraRef.current = false;
      const shouldFitCamera = !hasFitCameraRef.current && loadedModels.size === 0;
      const hasLoadedModelsAtStart = loadedModels.size > 0;
      const inputsToLoad = models.filter((input) => {
        const modelId = getModelInputId(input);
        return !loadedModels.has(modelId) && !loadingModelIdsRef.current.has(modelId);
      });

      if (inputsToLoad.length) {
        setLoadStatusThrottled({
          title: hasLoadedModelsAtStart ? "Laddar nya 3D-filer" : "Laddar 3D-vy",
          detail: `${inputsToLoad.length} frag-fil${inputsToLoad.length === 1 ? "" : "er"} ska byggas upp...`,
          blocking: !hasLoadedModelsAtStart,
        }, true);
      }

      for (let i = 0; i < inputsToLoad.length; i++) {
        const input = inputsToLoad[i];
        const modelId = getModelInputId(input);
        if (loadedModels.has(modelId) || loadingModelIdsRef.current.has(modelId) || cancelled) continue;

        loadingModelIdsRef.current.add(modelId);
        effectLoadingModelIds.add(modelId);
        notifyRetainedModelIds(loadedModels);
        setLoadStatusThrottled({
          title: `Laddar 3D-fil ${i + 1}/${inputsToLoad.length}`,
          detail: input.name,
          blocking: !hasLoadedModelsAtStart,
        }, true);
        await yieldToBrowser();
        if (cancelled) {
          loadingModelIdsRef.current.delete(modelId);
          continue;
        }

        let model: Awaited<ReturnType<FragmentsModels["load"]>>;
        try {
          model = await fragments.load(input.buffer, { modelId });
        } catch (error) {
          loadingModelIdsRef.current.delete(modelId);
          effectLoadingModelIds.delete(modelId);
          notifyRetainedModelIds(loadedModels);
          throw error;
        }
        loadingModelIdsRef.current.delete(modelId);
        effectLoadingModelIds.delete(modelId);
        if (!desiredModelIdsRef.current.has(modelId)) {
          await fragments.disposeModel(modelId);
          notifyRetainedModelIds(loadedModels);
          continue;
        }

        // IFC is Z-up; Three.js is Y-up — swap Y/Z on each child (matches working frag-viewer)
        model.object.traverse((child) => {
          const y = child.position.y;
          child.position.y = child.position.z;
          child.position.z = y;
        });
        scene.add(model.object);

        loadedModels.set(modelId, {
          model,
          name: input.name,
        });
        changed = true;
        needsRenderRef.current = true;
        notifyRetainedModelIds(loadedModels);
      }

      if (changed && sceneRef.current === ctx) {
        notifyRetainedModelIds(loadedModels);
        debugLog("[FragBimViewer] all models loaded, calling fragments.update");
        const currentFilter = filterRef.current;
        if (currentFilter !== undefined) {
          setLoadStatusThrottled({
            title: "Filtrerar 3D-vy",
            detail: "Döljer valda IFC-typer...",
            blocking: !hasLoadedModelsAtStart,
          }, true);
          await applyVisibilityFilterToModels(loadedModels, currentFilter);
        }
        setLoadStatusThrottled({
          title: "Bygger 3D-vy",
          detail: "Skapar synlig geometri och uppdaterar klippningen...",
          blocking: !hasLoadedModelsAtStart,
        }, true);
        await fragments.update(true);
        debugLog("[FragBimViewer] fragments.update done");
        setLoadedModelVersion((version) => version + 1);
        needsRenderRef.current = true;
        const ctx2 = sceneRef.current;
        if (ctx2 && shouldFitCamera) {
          setLoadStatusThrottled({
            title: "Renderar 3D-vy",
            detail: "Positionerar kameran...",
            blocking: true,
          }, true);
          fitCameraToScene(ctx2.camera, ctx2.controls, ctx2.loadedModels);
          hasFitCameraRef.current = true;
        }
        window.setTimeout(() => {
          if (!cancelled && sceneRef.current === ctx) setLoadStatus(null);
        }, 150);
      } else if (!changed && !loadingModelIdsRef.current.size && sceneRef.current === ctx) {
        setLoadStatus(null);
      }
    })().catch((error) => {
      console.error("[FragBimViewer] failed to load fragments", error);
      if (!cancelled && sceneRef.current === ctx) {
        setLoadStatus({
          title: "Kunde inte ladda 3D-vyn",
          detail: error instanceof Error ? error.message : "Ett okänt fel uppstod.",
          blocking: true,
          tone: "error",
        });
      }
    });

    return () => {
      cancelled = true;
      for (const modelId of effectLoadingModelIds) {
        fragments.abort(modelId);
        loadingModelIdsRef.current.delete(modelId);
      }
      effectLoadingModelIds.clear();
      if (sceneRef.current === ctx) notifyRetainedModelIds(ctx.loadedModels);
    };
  }, [sceneReady, modelsKey]);

  // --- Filter ---
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx) return;
    // filter=undefined means "no filter applied" — don't touch visibility
    if (filter === undefined) return;
    const { fragments, loadedModels } = ctx;
    if (loadedModels.size === 0) return;
    let cancelled = false;

    (async () => {
      await applyVisibilityFilterToModels(loadedModels, filter);
      if (cancelled) return;
      await fragments.update(true);
      needsRenderRef.current = true;
    })().catch((error) => {
      console.error("[FragBimViewer] failed to apply filter", error);
    });

    return () => { cancelled = true; };
  }, [filter]);

  // --- Color rules ---
  useEffect(() => {
    const ctx = sceneRef.current;
    if (!ctx) return;
    const { fragments, loadedModels } = ctx;

    (async () => {
      await applyColorRulesToModels(loadedModels, colorRules);
      await fragments.update(true);
    })();
  }, [colorRules]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      <LoadStatusOverlay status={loadStatus} />
      <DebugOverlay enabled={debugOverlay} debugInfo={debugInfo} />
    </div>
  );
};

export default React.memo(FragBimViewer);
